import {
  PracticeTemplate,
  ProgramBenchmarkName,
  ProgramTask,
  TaskDataTraitStatus,
} from '@cibo/core'
import { LandThumbnail } from '@cibo/landmanager'
import { useAuth, usePermissions } from '@cibo/profile'
import AddIcon from '@mui/icons-material/Add'
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import {
  alpha,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
  Stack,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material'
import { MouseEvent, PropsWithChildren, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../../../context'
import { usePracticeTemplates } from '../../../../../queries'

interface TemplateMenuProps {
  anchorEl: HTMLElement | null
  onClose: () => void
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName; traits: TaskDataTraitStatus[] }>
  onSelectTemplate: (template: PracticeTemplate, fieldId: string) => void
  onNewTemplate: () => void
  onEditTemplate: (templateId: string) => void
  selectedFieldId?: string | null
}

export const TemplateMenu = ({
  anchorEl,
  onClose,
  task,
  onSelectTemplate,
  onNewTemplate,
  onEditTemplate,
  selectedFieldId,
}: TemplateMenuProps) => {
  const { t } = useTranslation('@cibo/programs/TemplateEditorModal')
  const { userId: authUserId, persona } = useAuth()
  const { engagement, fieldModels, userId: farmerUserId } = useProgramEngagementContext()
  const programId = engagement.data?.programId ?? ''
  const isAdvisorOrAdmin = persona === 'advisor' || persona === 'admin'
  const { isDebugUser } = usePermissions()

  const [applyDialog, setApplyDialog] = useState<{ template: PracticeTemplate } | null>(null)
  const [applyFieldId, setApplyFieldId] = useState<string>(fieldModels.data?.items?.[0]?.id ?? '')

  const templates = usePracticeTemplates(
    { programId, taskId: task.id, ...(isAdvisorOrAdmin ? {} : { owner: authUserId }) },
    { enabled: !!anchorEl && !!programId }
  )

  const thisFarmerTemplates: PracticeTemplate[] = isAdvisorOrAdmin
    ? templates.data?.filter(t => t.owner === farmerUserId) ?? []
    : templates.data ?? []
  const otherFarmerTemplates = templates.data?.filter(t => t.owner === authUserId) ?? []
  const otherUserTemplates = templates.data?.filter(
    tmpl => tmpl.owner !== authUserId && tmpl.owner !== farmerUserId
  )
  const isLoading = templates.isLoading
  const isError = templates.isError

  const handleRowClick = (template: PracticeTemplate) => {
    const defaultFieldId = selectedFieldId ?? fieldModels.data?.items?.[0]?.id ?? ''
    setApplyFieldId(defaultFieldId)
    setApplyDialog({ template })
    onClose()
  }

  const handleEdit = (e: MouseEvent, templateId: string) => {
    e.stopPropagation()
    onEditTemplate(templateId)
    onClose()
  }

  const handleApplyConfirm = () => {
    if (applyDialog && applyFieldId) {
      onSelectTemplate(applyDialog.template, applyFieldId)
    }
    setApplyDialog(null)
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={onClose}
        slotProps={{ paper: { sx: { minWidth: 280, maxHeight: 480 } } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ borderBottom: theme => `1px solid ${theme.palette.divider}`, pl: 2, pr: 1, pb: 1 }}
        >
          <Typography fontWeight={600}>{t('Templates')}</Typography>
          <Stack direction="row" spacing={0}>
            <IconButton
              onClick={() => {
                onNewTemplate()
                onClose()
              }}
              aria-label={t('newTemplate')}
            >
              <AddIcon />
            </IconButton>
            <IconButton onClick={onClose} aria-label={t('close')}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
        <TemplateMenuSection
          title={t('thisFarmerSection')}
          templates={thisFarmerTemplates}
          handleRowClick={handleRowClick}
          handleEdit={handleEdit}
          isLoading={isLoading}
        >
          {isLoading && (
            <MenuItem disabled>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="body2">{t('loading')}</Typography>
            </MenuItem>
          )}
          {isError && (
            <MenuItem disabled>
              <Typography variant="body2" color="error">
                {t('loadError')}
              </Typography>
            </MenuItem>
          )}
          {!isLoading && thisFarmerTemplates.length === 0 && (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {t('noTemplates')}
              </Typography>
            </MenuItem>
          )}
        </TemplateMenuSection>

        {isAdvisorOrAdmin && (
          <TemplateMenuSection
            title={t('anyFarmerSection')}
            templates={otherFarmerTemplates}
            isLoading={isLoading}
            handleRowClick={handleRowClick}
            handleEdit={handleEdit}
          />
        )}
        {isAdvisorOrAdmin && isDebugUser && (
          <TemplateMenuSection
            title={t('otherUsersSection')}
            templates={otherUserTemplates}
            isLoading={isLoading}
            handleRowClick={handleRowClick}
            handleEdit={handleEdit}
            debugOnly
          />
        )}
      </Menu>

      {/* Apply template dialog */}
      <Dialog open={!!applyDialog} onClose={() => setApplyDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {t('applyTemplateDialogTitle', { name: applyDialog?.template.name })}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>{t('applyTemplateFieldLabel')}</InputLabel>
            <Select
              value={applyFieldId}
              label={t('applyTemplateFieldLabel')}
              onChange={e => setApplyFieldId(e.target.value)}
            >
              {fieldModels.data?.items?.map(field => (
                <MenuItem key={field.id} value={field.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LandThumbnail field={field} width={32} height={32} />
                    <Stack>
                      <Typography>{field.name}</Typography>
                      <Typography variant="caption">
                        {field.acres?.toFixed(1) ?? 0} acres
                      </Typography>
                    </Stack>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialog(null)}>{t('cancel')}</Button>
          <Button variant="contained" disabled={!applyFieldId} onClick={handleApplyConfirm}>
            {t('applyTemplate')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const TemplateMenuRow = ({
  template,
  handleRowClick,
  handleEdit,
  sx,
}: {
  template: PracticeTemplate
  handleRowClick: (template: PracticeTemplate) => void
  handleEdit: (e: MouseEvent, id: string) => void
  sx?: SxProps
}) => {
  const { t } = useTranslation('@cibo/programs/TemplateEditorModal')
  return (
    <MenuItem
      data-testid={`template-menu-row-${template.name}`}
      key={template.id}
      onClick={() => handleRowClick(template)}
      sx={{
        '& .edit-btn': { visibility: 'hidden' },
        '&:hover .edit-btn': { visibility: 'visible' },
        ...sx,
      }}
    >
      <ListItemText primary={template.name} slotProps={{ primary: { noWrap: true } }} />
      <IconButton
        className="edit-btn"
        size="small"
        onClick={e => handleEdit(e, template.id)}
        sx={{ ml: 1 }}
        aria-label={t('editTemplate')}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </MenuItem>
  )
}

const TemplateMenuSection = ({
  title,
  templates,
  handleRowClick,
  handleEdit,
  isLoading,
  debugOnly = false,
  children,
}: PropsWithChildren<{
  title: string
  templates?: PracticeTemplate[]
  handleRowClick: (template: PracticeTemplate) => void
  handleEdit: (e: MouseEvent, id: string) => void
  isLoading: boolean
  debugOnly?: boolean
}>) => {
  const { t } = useTranslation('@cibo/programs/TemplateEditorModal')
  const { palette } = useTheme()

  return (
    <>
      <Divider />
      <ListSubheader
        disableSticky
        sx={
          debugOnly
            ? {
                bgcolor: alpha(palette.error.main, 0.2),
              }
            : {}
        }
      >
        {debugOnly && <BugReportOutlinedIcon fontSize="small" color="error" />}
        {title}
      </ListSubheader>
      {children}

      {!isLoading && templates?.length === 0 && (
        <MenuItem disabled>
          <Typography variant="body2" color="text.secondary">
            {t('noTemplates')}
          </Typography>
        </MenuItem>
      )}

      {templates?.map(row => (
        <TemplateMenuRow
          key={row.id}
          template={row}
          handleRowClick={handleRowClick}
          handleEdit={handleEdit}
          sx={
            debugOnly
              ? {
                  bgcolor: alpha(palette.error.main, 0.2),
                }
              : {}
          }
        />
      ))}
    </>
  )
}
